import {
  IColumn,
  Stack,
  Label,
  DetailsList,
  SelectionMode,
  Link,
  Breadcrumb,
  Text,
  Separator,
} from '@fluentui/react';
import { useState, useEffect } from 'react';
import {
  IPagedCollection,
  IAsset,
  getAssets,
  getAssetParents,
} from '../../services/assetServices';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { formatDate } from '@moko/utils';
import { errorMessageAtom } from '../../atoms/messageBarAtoms';

interface AssetHierarchyProps {
  assetId: number;
}

const AssetHierarchy: React.FC<AssetHierarchyProps> = ({ assetId }) => {
  let navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [parentHierarchy, setParentHierarchy] = useState<
    { id: number; name: string }[]
  >([]);
  const [childAssets, setChildAssets] = useState<IAsset[]>([]);
  const setErrorMessage = useSetAtom(errorMessageAtom);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const abortController = new AbortController();
        const parents = await getAssetParents(abortController, assetId);
        setParentHierarchy(parents.reverse());

        const data: IPagedCollection<IAsset> = await getAssets(
          abortController,
          10,
          1,
          undefined,
          undefined,
          encodeURIComponent(
            JSON.stringify({
              filters: [
                {
                  column: 'PARENT_ID',
                  operation: 'Eq',
                  value: assetId.toString(),
                  type: 'Number',
                },
              ],
            })
          )
        );
        setChildAssets(data.items);
      } catch (error: any) {
        setErrorMessage(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    setIsLoading(false);
  }, [assetId]);

  const _columns: IColumn[] = [
    {
      key: 'Id',
      name: 'Id',
      fieldName: 'id',
      minWidth: 45,
      maxWidth: 50,
      isResizable: true,
      onRender: (item) => (
        <Link
          key={item.id}
          onClick={() => navigate(`/editAsset?id=${item.id}`)}
        >
          {item.id}
        </Link>
      ),
    },
    {
      key: 'Code',
      name: 'Code',
      fieldName: 'code',
      minWidth: 75,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: 'Name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 175,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: 'category',
      name: 'Category',
      fieldName: 'category',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'AssetDate',
      name: 'Asset Date',
      fieldName: 'changeDate',
      minWidth: 100,
      maxWidth: 125,
      isResizable: true,
      onRender: (item: IAsset) =>
        item.changeDate ? formatDate(String(item.changeDate), true) : '',
    },
  ];

  return (
    <Stack tokens={{ childrenGap: 10 }}>
      <Label>Hierarchy for Asset ID: {assetId}</Label>
      {isLoading ? (
        <Label>Loading...</Label>
      ) : (
        <>
          <Separator alignContent="start">Parent assets</Separator>
          {parentHierarchy.length > 0 ? (
            <Breadcrumb
              items={parentHierarchy.map((parent) => ({
                key: parent.id.toString(),
                text: parent.name,
                onClick: () => navigate(`/editAsset?id=${parent.id}`),
              }))}
              maxDisplayedItems={5}
              ariaLabel="Asset hierarchy breadcrumb"
            />
          ) : (
            <Text variant="large">No parent</Text>
          )}
          <Separator alignContent="start">Child assets</Separator>
          <DetailsList
            items={childAssets}
            columns={_columns}
            selectionMode={SelectionMode.none}
          />
        </>
      )}
    </Stack>
  );
};

export default AssetHierarchy;
